if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        if (!this.form) return;

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.submitButton = this.querySelector(
          '[type="submit"][name="add"]'
        );

        if (!this.submitButton) return;

        this.submitButtonText =
          this.getSubmitButtonTextElement();

        this.loadingSpinner =
          this.querySelector('.loading__spinner');

        this.originalButtonText =
          this.submitButtonText?.textContent?.trim() ||
          'Add to basket';

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.hideErrors =
          this.dataset.hideErrors === 'true';

        this.error = false;
      }

      getSubmitButtonTextElement() {
        let buttonText =
          this.submitButton.querySelector(
            '.cw-product-form__button-label'
          ) ||
          this.submitButton.querySelector(
            'span:not(.sold-out-message):not(.loading__spinner):not(.svg-wrapper)'
          );

        if (buttonText) return buttonText;

        buttonText = document.createElement('span');
        buttonText.className =
          'cw-product-form__button-label';

        const directTextNodes = Array.from(
          this.submitButton.childNodes
        ).filter(
          (node) =>
            node.nodeType === Node.TEXT_NODE &&
            node.textContent.trim() !== ''
        );

        const existingText = directTextNodes
          .map((node) => node.textContent.trim())
          .join(' ')
          .trim();

        directTextNodes.forEach((node) =>
          node.remove()
        );

        buttonText.textContent =
          existingText ||
          window.variantStrings?.addToCart ||
          'Add to basket';

        this.submitButton.prepend(buttonText);

        return buttonText;
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (
          !this.submitButton ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }

        const variantIdInput = this.variantIdInput;

        if (
          !variantIdInput ||
          !variantIdInput.value
        ) {
          this.handleErrorMessage(
            'Please select a product option before adding it to the basket.'
          );
          return;
        }

        this.handleErrorMessage();
        this.error = false;
        this.setLoadingState(true);

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                },
              };

        config.method = 'POST';
        config.headers = config.headers || {};
        config.headers.Accept =
          'application/json';
        config.headers['X-Requested-With'] =
          'XMLHttpRequest';

        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        config.body = formData;

        const variantId = formData.get('id');
        const quantity =
          Number.parseInt(
            formData.get('quantity'),
            10
          ) || 1;

        const linesUpdateDeferred =
          this.createCartLinesUpdateEvent(
            variantId,
            quantity
          );

        const cartAddUrl =
          window.routes?.cart_add_url ||
          '/cart/add';

        try {
          const response = await fetch(
            cartAddUrl,
            config
          );

          const responseData =
            await this.parseResponse(response);

          if (
            !response.ok ||
            responseData.status
          ) {
            const errorMessage =
              responseData.errors ||
              responseData.description ||
              responseData.message ||
              'This product could not be added to the basket.';

            this.handleErrorMessage(
              errorMessage
            );

            this.dispatchCartErrorEvent(
              errorMessage,
              'INVALID'
            );

            linesUpdateDeferred?.reject(
              new Error(errorMessage)
            );

            this.error = true;
            return;
          }

          this.resolveCartLinesUpdate(
            linesUpdateDeferred
          );

          await this.updateHeaderBasket();

          this.closeCartNotification();

          this.showAddedConfirmation();
        } catch (error) {
          console.error(
            'Cylinder Warehouse add-to-basket error:',
            error
          );

          const errorMessage =
            error?.message ||
            'A network error prevented the product from being added.';

          this.handleErrorMessage(
            errorMessage
          );

          this.dispatchCartErrorEvent(
            errorMessage,
            'SERVICE_UNAVAILABLE'
          );

          linesUpdateDeferred?.reject(error);
          this.error = true;
        } finally {
          if (this.error) {
            this.setLoadingState(false);
          }

          if (
            window.CartPerformance &&
            typeof window.CartPerformance
              .measureFromEvent === 'function'
          ) {
            window.CartPerformance.measureFromEvent(
              'add:user-action',
              event
            );
          }
        }
      }

      setLoadingState(isLoading) {
        if (!this.submitButton) return;

        if (isLoading) {
          this.submitButton.disabled = true;

          this.submitButton.setAttribute(
            'aria-disabled',
            'true'
          );

          this.submitButton.setAttribute(
            'aria-busy',
            'true'
          );

          this.submitButton.classList.add(
            'loading'
          );

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              'Adding...';
          }

          if (this.loadingSpinner) {
            this.loadingSpinner.classList.remove(
              'hidden'
            );
          }
        } else {
          this.submitButton.disabled = false;

          this.submitButton.removeAttribute(
            'aria-disabled'
          );

          this.submitButton.removeAttribute(
            'aria-busy'
          );

          this.submitButton.classList.remove(
            'loading'
          );

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              this.originalButtonText;
          }

          if (this.loadingSpinner) {
            this.loadingSpinner.classList.add(
              'hidden'
            );
          }
        }
      }

      showAddedConfirmation() {
        if (!this.submitButtonText) {
          this.setLoadingState(false);
          return;
        }

        this.submitButton.classList.remove(
          'loading'
        );

        this.submitButton.removeAttribute(
          'aria-busy'
        );

        if (this.loadingSpinner) {
          this.loadingSpinner.classList.add(
            'hidden'
          );
        }

        this.submitButtonText.textContent =
          'Added to basket';

        window.setTimeout(() => {
          this.setLoadingState(false);
        }, 1200);
      }

      async updateHeaderBasket() {
        const cartUrl =
          window.routes?.cart_url || '/cart';

        const response = await fetch(
          `${cartUrl}.js`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              'X-Requested-With':
                'XMLHttpRequest',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            'The product was added, but the basket total could not be refreshed.'
          );
        }

        const cart = await response.json();

        const basketLink =
          document.querySelector(
            '.cw-header-cart'
          );

        const countElement =
          document.querySelector(
            '.cw-header-cart__count'
          );

        const totalElement =
          document.querySelector(
            '.cw-header-cart__total'
          );

        if (countElement) {
          countElement.textContent =
            `(${cart.item_count})`;
        }

        if (totalElement) {
          totalElement.textContent =
            this.formatMoney(
              cart.total_price,
              cart.currency
            );
        }

        if (basketLink) {
          const itemWord =
            cart.item_count === 1
              ? 'item'
              : 'items';

          const basketLabel =
            `Basket: ${cart.item_count} ${itemWord}, ` +
            this.formatMoney(
              cart.total_price,
              cart.currency
            );

          basketLink.setAttribute(
            'aria-label',
            basketLabel
          );

          const hiddenText =
            basketLink.querySelector(
              '.visually-hidden'
            );

          if (hiddenText) {
            hiddenText.textContent =
              basketLabel;
          }
        }

        document.dispatchEvent(
          new CustomEvent(
            'cw:basket-updated',
            {
              detail: {
                cart,
              },
            }
          )
        );
      }

      formatMoney(amount, currency) {
        const numericAmount =
          Number(amount || 0) / 100;

        try {
          return new Intl.NumberFormat(
            'en-GB',
            {
              style: 'currency',
              currency:
                currency ||
                window.Shopify?.currency
                  ?.active ||
                'GBP',
            }
          ).format(numericAmount);
        } catch (_error) {
          return `£${numericAmount.toFixed(2)}`;
        }
      }

      closeCartNotification() {
        const notification =
          document.querySelector(
            'cart-notification'
          );

        if (!notification) return;

        if (
          typeof notification.close ===
          'function'
        ) {
          notification.close();
        }

        notification.removeAttribute(
          'active'
        );

        notification.removeAttribute(
          'open'
        );

        notification
          .classList
          .remove('active');

        notification
          .classList
          .remove('animate');
      }

      async parseResponse(response) {
        const contentType =
          response.headers.get(
            'content-type'
          ) || '';

        if (
          contentType.includes(
            'application/json'
          )
        ) {
          return response.json();
        }

        const responseText =
          await response.text();

        try {
          return JSON.parse(
            responseText
          );
        } catch (_error) {
          return {
            status: response.status,
            description:
              responseText ||
              'The basket could not be updated.',
          };
        }
      }

      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) {
          if (errorMessage) {
            console.error(errorMessage);
          }

          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            typeof errorMessage === 'string'
              ? errorMessage
              : JSON.stringify(
                  errorMessage
                );
        }
      }

      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (
            text &&
            this.submitButtonText
          ) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings
                ?.addToCart ||
              'Add to basket';
          }
        }
      }

      createCartLinesUpdateEvent(
        variantId,
        quantity
      ) {
        const {
          CartLinesUpdateEvent,
        } = window.StandardEvents || {};

        if (
          !CartLinesUpdateEvent ||
          typeof CartLinesUpdateEvent
            .createPromise !== 'function'
        ) {
          return null;
        }

        const deferred =
          CartLinesUpdateEvent
            .createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [
              {
                merchandiseId:
                  variantId,
                quantity,
              },
            ],
            promise:
              deferred.promise,
          })
        );

        return deferred;
      }

      resolveCartLinesUpdate(
        deferred
      ) {
        if (!deferred) return;

        const {
          CartLinesUpdateEvent,
        } = window.StandardEvents || {};

        if (!CartLinesUpdateEvent) {
          return;
        }

        const cartUrl =
          window.routes?.cart_url ||
          '/cart';

        const pendingCartDataPromise =
          typeof CartItems !==
            'undefined' &&
          typeof CartItems
            .fetchCartData ===
            'function'
            ? CartItems.fetchCartData()
            : fetch(
                `${cartUrl}.json`
              ).then((response) =>
                response.json()
              );

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              deferred.reject(
                new Error(
                  'Missing currency in cart response'
                )
              );

              return;
            }

            const cartData =
              typeof CartLinesUpdateEvent
                .createCartFromAjaxResponse ===
                'function'
                ? CartLinesUpdateEvent
                    .createCartFromAjaxResponse(
                      cart
                    )
                : cart;

            deferred.resolve({
              cart: cartData,
            });
          })
          .catch((error) =>
            deferred.reject(error)
          );
      }

      dispatchCartErrorEvent(
        message,
        code
      ) {
        const {
          CartErrorEvent,
        } = window.StandardEvents || {};

        if (!CartErrorEvent) return;

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code,
          })
        );
      }

      get variantIdInput() {
        return this.form?.querySelector(
          '[name="id"]'
        );
      }
    }
  );
}